/**
 * Shared UI formatting helpers & color maps.
 *
 * Extracted from app.ts and tab-profile.ts (R5) to eliminate duplication
 * and provide a single source of truth for badge rendering and color
 * constants used across the web presentation layer.
 */

import type { Schedule, Task } from '../models/types';
import { Level } from '../models/types';
import { fmtTime, stripTaskNameAffixes } from '../utils/date-utils';
import { sanitizeColor } from './certification-utils';
import { getCertColor, getCertificationById, getCertLabel, getTemplateVisualMap } from './config-store';

// ─── SVG Icons ───────────────────────────────────────────────────────────────

export const SVG_ICONS = {
  edit: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>`,
  trash: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>`,
  block: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>`,
  moon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`,
  sun: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`,
  participants: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  tasks: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></svg>`,
  chart: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`,
  settings: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
  calendar: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
  home: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12 12 3l9 9"/><path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10"/></svg>`,
  schedule: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/></svg>`,
  lock: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
  chevronDown: `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`,
};

// ─── Task Visual Helpers ────────────────────────────────────────────────────

/** Get color for a task-like object. Uses direct color property, falls back to template visual map by sourceName. */
export function getTaskColor(task: { color?: string; sourceName?: string }): string {
  if (task.color) return sanitizeColor(task.color);
  if (task.sourceName) {
    return sanitizeColor(getTemplateVisualMap()[task.sourceName]?.color || '#7f8c8d');
  }
  return '#7f8c8d';
}

/** Get label for a task-like object. Uses sourceName (template name). */
export function getTaskLabel(task: { sourceName?: string; name?: string }): string {
  return task.sourceName || task.name || 'משימה';
}

/** Dynamic group→color palette (auto-assigned on first access). */
const GROUP_PALETTE = ['#3498db', '#e67e22', '#2ecc71', '#9b59b6', '#e74c3c', '#1abc9c', '#f39c12', '#34495e'];
const _groupColorCache: Record<string, string> = {};

export function groupColor(group: string): string {
  if (!_groupColorCache[group]) {
    const idx = Object.keys(_groupColorCache).length % GROUP_PALETTE.length;
    _groupColorCache[group] = GROUP_PALETTE[idx];
  }
  return _groupColorCache[group];
}

export const LEVEL_COLORS: Record<Level, string> = {
  [Level.L0]: '#95a5a6',
  [Level.L2]: '#2ecc71',
  [Level.L3]: '#e67e22',
  [Level.L4]: '#e74c3c',
};

// ─── Formatting Helpers ──────────────────────────────────────────────────────

/** Format a Date as HH:MM (24h). Delegates to shared fmtTime. */
export function fmt(d: Date): string {
  return fmtTime(d);
}

/**
 * Format a provenance/save timestamp for a METADATA surface ONLY — a saved
 * library entity's `createdAt` (preset / participant-set / task-set / snapshot)
 * or an export envelope's `exportedAt`. It renders a real calendar date and is
 * therefore BANNED on any schedule-day surface: schedule days are `יום N`, never
 * a date. It accepts only a raw timestamp (`number | Date`) — never a Task,
 * Schedule, periodStart/generatedAt anchor, or day index — so it cannot be fed a
 * schedule day. `withTime` appends ", HH:MM". No weekday name, no relative form.
 */
export function formatMetadataDate(ms: number | Date, withTime = false): string {
  const d = ms instanceof Date ? ms : new Date(ms);
  if (!Number.isFinite(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = withTime
    ? { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short' };
  return d.toLocaleDateString('he-IL', opts);
}

/** Level badge HTML. */
export function levelBadge(level: Level): string {
  const labels: Record<Level, string> = {
    [Level.L0]: 'דרגה 0',
    [Level.L2]: 'דרגה 2',
    [Level.L3]: 'דרגה 3',
    [Level.L4]: 'דרגה 4',
  };
  return `<span class="badge" style="background:${LEVEL_COLORS[level]}">${labels[level]}</span>`;
}

/** Single certification badge HTML. Shows orphan warning if cert definition was deleted. */
export function certBadge(c: string): string {
  const def = getCertificationById(c);
  if (!def) {
    return `<span class="badge badge-orphan" title="הסמכה שנמחקה: ${escHtml(c)}">⚠ ${escHtml(c)}</span>`;
  }
  if (def.deleted) {
    return `<span class="badge badge-orphan" title="הסמכה שנמחקה: ${escHtml(def.label)}">⚠ ${escHtml(def.label)}</span>`;
  }
  return `<span class="badge" style="background:${sanitizeColor(def.color)}">${escHtml(def.label)}</span>`;
}

/** Multiple certification badges (returns dash when empty). */
export function certBadges(certs: string[], emptyLabel = '—'): string {
  if (certs.length === 0) return `<span class="text-muted">${emptyLabel}</span>`;
  return certs.map((c) => certBadge(c)).join(' ');
}

/** Group badge HTML. Optionally renders as a clickable filter badge. */
export function groupBadge(group: string, clickable = false): string {
  const color = groupColor(group);
  if (clickable) {
    return `<span class="badge badge-group-select" style="background:${color};cursor:pointer" data-select-group="${escAttr(group)}" title="בחר את כל חברי ${escAttr(group)}">${escHtml(group)}</span>`;
  }
  return `<span class="badge" style="background:${color}">${escHtml(group)}</span>`;
}

/** Task badge HTML from a task-like object with direct visual properties. */
export function taskBadge(task: { color?: string; sourceName?: string; name?: string }): string {
  const color = sanitizeColor(task.color);
  const label = task.sourceName || (task.name ? stripDayPrefix(task.name) : '');
  return `<span class="badge" style="background:${color}">${escHtml(label)}</span>`;
}

/**
 * Strip the engine-internal `D{N} ` day-index prefix and any trailing numeric
 * `משמרת {N}` suffix from a task name. Times are always rendered separately,
 * so the numeric shift adds noise without information. Descriptive shift
 * labels (e.g. `משמרת בוקר`) are preserved because the trailing token isn't
 * numeric.
 *
 * Prefer `task.sourceName` when you have a `Task` object — this helper is the
 * fallback for transport objects that only carry the decorated `name` string.
 */
export const stripDayPrefix = stripTaskNameAffixes;

/**
 * Strip a trailing `HH:MM–HH:MM` range (hyphen, en-dash, or minus sign) from
 * a user-defined slot label. The UI renders time separately as an aligned
 * LTR tag, so including it inside the label is pure duplication. Anchored to
 * the end of the string, so mid-label time references are preserved.
 */
export function cleanSlotLabel(label: string): string {
  return label.replace(/\s+\d{1,2}:\d{2}\s*[-–−]\s*\d{1,2}:\d{2}\s*$/, '').trim();
}

/** Escape a string for safe HTML interpolation. */
export function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Escape a string for safe HTML attribute interpolation. */
export function escAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;');
}

// ─── Shift-Split Display Helpers ────────────────────────────────────────────

export interface SplitDisplay {
  /** 1 = first half, 2 = second half. */
  part: 1 | 2;
  /** Human ordinal: "חצי ראשון" / "חצי שני". */
  halfLabel: string;
  /** This half's coverage window, "HH:MM–HH:MM". */
  thisWindow: string;
  /** Full (pre-split) shift window, "HH:MM–HH:MM". */
  fullWindow: string;
  /** The sibling half's coverage window, when resolvable from the schedule. */
  otherWindow?: string;
  /** Name(s) covering the other half (normally exactly one). */
  otherNames: string[];
}

/**
 * Resolve display metadata for a split-half task; returns `null` for ordinary
 * whole tasks. `splitOriginalMs` + `splitPart` alone reconstruct the full
 * pre-split window (part 1 is anchored at the original start, part 2 at the
 * original end), so the sibling lookup — keyed on the shared `splitGroupId` —
 * is only needed to name who covers the other half.
 */
export function getSplitDisplay(task: Task, schedule?: Schedule | null): SplitDisplay | null {
  if (task.splitGroupId === undefined || (task.splitPart !== 1 && task.splitPart !== 2)) {
    return null;
  }
  const part = task.splitPart;
  const startMs = task.timeBlock.start.getTime();
  const endMs = task.timeBlock.end.getTime();
  const origMs = task.splitOriginalMs ?? (endMs - startMs) * 2;
  const fullStartMs = part === 1 ? startMs : endMs - origMs;
  const fullEndMs = part === 1 ? startMs + origMs : endMs;

  const otherNames: string[] = [];
  let otherWindow: string | undefined;
  if (schedule) {
    const other = schedule.tasks.find((t) => t.splitGroupId === task.splitGroupId && t.splitPart !== part);
    if (other) {
      otherWindow = `${fmt(other.timeBlock.start)}–${fmt(other.timeBlock.end)}`;
      const nameById = new Map(schedule.participants.map((p) => [p.id, p.name] as const));
      for (const a of schedule.assignments) {
        if (a.taskId !== other.id) continue;
        const nm = nameById.get(a.participantId);
        if (nm) otherNames.push(nm);
      }
    }
  }

  return {
    part,
    halfLabel: part === 1 ? 'חצי ראשון' : 'חצי שני',
    thisWindow: `${fmt(task.timeBlock.start)}–${fmt(task.timeBlock.end)}`,
    fullWindow: `${fmt(new Date(fullStartMs))}–${fmt(new Date(fullEndMs))}`,
    otherWindow,
    otherNames,
  };
}

/**
 * Compact, low-key "½" pill — the at-a-glance cue that a slot was split.
 * Calm by design (a feature detail, not a warning); the full story lives in
 * the tap target's {@link splitInfoBlock}.
 */
export function splitBadge(): string {
  return `<span class="split-badge" title="משבצת זו פוצלה לשני חצאים בין שני אנשים — הקש לפרטים">½</span>`;
}

/**
 * Structured, quiet split explainer for tap targets (grid bottom sheet, task
 * tooltip): the full shift, this half, and who covers the other half — so a
 * normal user understands why one shift shows as two people.
 */
export function splitInfoBlock(sd: SplitDisplay): string {
  const other =
    sd.otherNames.length > 0
      ? `${escHtml(sd.otherNames.join(', '))}${
          sd.otherWindow ? ` <span class="split-info-time" dir="ltr">${sd.otherWindow}</span>` : ''
        }`
      : sd.otherWindow
        ? `<span class="split-info-time" dir="ltr">${sd.otherWindow}</span>`
        : 'טרם אויש';
  return `<div class="split-info">
    <div class="split-info-title">½ משבצת מפוצלת · ${sd.halfLabel}</div>
    <div class="split-info-row"><span class="split-info-k">משמרת מלאה</span><span class="split-info-v" dir="ltr">${sd.fullWindow}</span></div>
    <div class="split-info-row"><span class="split-info-k">החצי הזה</span><span class="split-info-v" dir="ltr">${sd.thisWindow}</span></div>
    <div class="split-info-row"><span class="split-info-k">החצי השני</span><span class="split-info-v">${other}</span></div>
  </div>`;
}

// ─── Theme Utilities ────────────────────────────────────────────────────────

const THEME_STORAGE_KEY = 'gardenmanager_theme';
const DEFAULT_ATTEMPTS_STORAGE_KEY = 'gardenmanager_default_attempts';
const FALLBACK_DEFAULT_ATTEMPTS = 60;
/**
 * Upper safety bound on the optimization-attempt count. The two number inputs
 * that drive it — the schedule toolbar `#input-scenarios` and the settings
 * `#input-default-attempts` — carry a `min` but no `max`, and neither the
 * `doGenerate` read nor the settings handler bounded the value from above, so a
 * pathological entry (e.g. 999999) would drive effectively-unbounded solver work.
 * The cap is far above any real need (default 60) and above the 4000 the e2e
 * specs fill to slow a run for cancellation.
 */
export const MAX_DEFAULT_ATTEMPTS = 10000;

export function applyTheme(theme: 'dark' | 'light'): void {
  if (theme === 'light') {
    document.documentElement.dataset.theme = 'light';
  } else {
    delete document.documentElement.dataset.theme;
  }
}

export function getStoredTheme(): 'dark' | 'light' {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  const isMobileOrTablet = window.matchMedia?.('(max-width: 1024px)').matches;
  return isMobileOrTablet ? 'light' : 'dark';
}

export function getCurrentTheme(): 'dark' | 'light' {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export function setTheme(theme: 'dark' | 'light'): void {
  // Write to localStorage immediately so subsequent renders read the correct value.
  // Swallow quota / access errors so the theme toggle still flips the UI —
  // a missing persisted theme is a far smaller problem than a broken click handler.
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (err) {
    console.warn('[ui-helpers] Failed to persist theme:', err);
  }

  const commitSwitch = () => {
    applyTheme(theme);
  };

  if (typeof document.startViewTransition === 'function') {
    document.startViewTransition(commitSwitch);
  } else {
    commitSwitch();
  }
}

// ─── Default Attempts Utilities ─────────────────────────────────────────────

export function getStoredDefaultAttempts(): number {
  const stored = localStorage.getItem(DEFAULT_ATTEMPTS_STORAGE_KEY);
  if (stored !== null) {
    const val = parseInt(stored, 10);
    if (Number.isInteger(val) && val > 0) return Math.min(val, MAX_DEFAULT_ATTEMPTS);
  }
  return FALLBACK_DEFAULT_ATTEMPTS;
}

export function setDefaultAttempts(value: number): void {
  // Clamp to [1, MAX_DEFAULT_ATTEMPTS] on the way in so storage can never hold a
  // non-positive or pathologically large count, regardless of the caller.
  const clamped = Math.min(Math.max(1, Math.floor(value)), MAX_DEFAULT_ATTEMPTS);
  try {
    localStorage.setItem(DEFAULT_ATTEMPTS_STORAGE_KEY, String(clamped));
  } catch (err) {
    console.warn('[ui-helpers] Failed to persist default attempts:', err);
  }
}
